import { motion } from "framer-motion";
import { PenTool, Palette, Package, Rocket, ArrowRight } from "lucide-react";
import { fadeUp, stagger, viewport } from "../lib/motion";
import { useQuote } from "../components/site/Header";
import Breadcrumb from "../components/site/Breadcrumb";

const IMG = "https://customer-assets-jai6qajn.emergentagent.net/job_business-food-pack/artifacts/zuk7xugx_Custom%20Branding.png";

const STEPS = [
  { icon: PenTool, t: "Share your brand", d: "Send us your logo, colours and the products you want branded." },
  { icon: Palette, t: "We design", d: "Our team mocks up print-ready artwork on your chosen packaging." },
  { icon: Package, t: "Approve & print", d: "Approve the proof and we produce your custom run with quality checks." },
  { icon: Rocket, t: "Delivered to you", d: "Branded packaging shipped in bulk, ready to wow your customers." },
];

export default function CustomBranding() {
  const openQuote = useQuote();
  return (
    <div>
      <section className="bg-[#F8FAF8] border-b border-[#E2E8E4] pt-12 pb-16">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <Breadcrumb items={[{ label: "Custom Branding" }]} />
          <div className="grid lg:grid-cols-12 gap-12 items-center mt-8">
            <div className="lg:col-span-6">
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#1D6B2A] mb-3">
                Custom branding
              </p>
              <h1 className="font-head text-4xl md:text-6xl font-medium tracking-tighter text-[#0A1C0E]">
                Your brand, on every box
              </h1>
              <p className="text-lg text-[#3A4D3E] mt-5 max-w-xl">
                Custom-printed cups, boxes, bags and more. Turn every order into a moment of brand
                recall and free marketing — especially powerful for delivery-first cloud kitchens.
              </p>
              <button
                onClick={() => openQuote("Custom Branding")}
                data-testid="branding-quote-btn"
                className="mt-8 bg-[#1D6B2A] text-white rounded-full px-8 py-4 font-semibold hover:bg-[#154d1e] transition-colors flex items-center gap-2"
              >
                Start your custom order <ArrowRight size={18} />
              </button>
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-6 rounded-[2rem] overflow-hidden aspect-[4/3] shadow-xl"
            >
              <img src={IMG} alt="Custom branded packaging" className="w-full h-full object-cover" />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="text-center mb-14">
            <h2 className="font-head text-4xl md:text-5xl font-medium tracking-tight text-[#0A1C0E]">
              How it works
            </h2>
          </div>
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {STEPS.map((s, i) => (
              <motion.div key={s.t} variants={fadeUp} custom={i} className="relative bg-white border border-[#E2E8E4] rounded-2xl p-7">
                <span className="absolute top-5 right-6 font-editorial text-3xl text-[#E2E8E4]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="grid place-items-center w-12 h-12 rounded-xl bg-[#FDCD00] text-[#0A1C0E]">
                  <s.icon size={24} />
                </span>
                <h3 className="font-head text-xl font-semibold text-[#0A1C0E] mt-4">{s.t}</h3>
                <p className="text-[#3A4D3E] mt-2 text-sm">{s.d}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
